/**
 * Generates the iframe-side bridge script as a string for embedding in the
 * assembled iframe document. Handles click, editing, paste, drag, and
 * theme update events inside the iframe, communicating with parent via postMessage.
 */

import { generateFormatEngineScript } from './format-engine.js';
import { generatePasteSanitizerScript } from './paste-sanitizer.js';
import {
  CLIENT_LINK_BINDINGS,
  CLIENT_TEXT_BINDINGS,
  CLIENT_LINK_MARKER_PREFIX
} from '../shared/client-info.js';
import { getScrollAnimationRuntimeSource } from '../shared/scroll-animation-runtime-source.js';
import { TOKEN_PRIORITY } from '../shared/theme-tokens.js';
import { KIND_ATTR, KIND_LABELS } from '../shared/element-kind.js';

import { coreStateLayer } from './iframe-bridge-layers/core-state.js';
import { reorderPreviewLayer } from './iframe-bridge-layers/reorder-preview.js';
import { interactionLayer } from './iframe-bridge-layers/interaction.js';
import { editingInputLayer } from './iframe-bridge-layers/editing-input.js';
import { messageHandlerLayer } from './iframe-bridge-layers/message-handler.js';
import { overlayReportersLayer } from './iframe-bridge-layers/overlay-reporters.js';
import { collabReporterLayer } from './iframe-bridge-layers/collab-reporter.js';

export function generateIframeBridgeScript(nonce, options = {}) {
  const formatEngineScript = generateFormatEngineScript();
  const pasteSanitizerScript = generatePasteSanitizerScript({
    guestFrame: options.guestFrame === true
  });
  const scrollAnimationRuntimeScript = getScrollAnimationRuntimeSource();
  const iconTinting = options.iconTinting !== false;
  const reorderableSelectors = Array.isArray(options.reorderableSelectors)
    ? options.reorderableSelectors.filter(
        (selector) => typeof selector === 'string' && selector.trim()
      )
    : [];
  const reorderableSpecs = Array.isArray(options.reorderableSpecs)
    ? options.reorderableSpecs
        .map((spec) => {
          const kind = typeof spec?.kind === 'string' ? spec.kind.trim() : '';
          const itemSelector =
            typeof spec?.itemSelector === 'string' ? spec.itemSelector.trim() : '';
          const parentSelector =
            typeof spec?.parentSelector === 'string' ? spec.parentSelector.trim() : '';
          const sourceSelector =
            typeof spec?.sourceSelector === 'string' ? spec.sourceSelector.trim() : '';
          const siblingSelector =
            typeof spec?.siblingSelector === 'string'
              ? spec.siblingSelector.trim()
              : sourceSelector;
          const requiredAncestorSelector =
            typeof spec?.requiredAncestorSelector === 'string'
              ? spec.requiredAncestorSelector.trim()
              : '';
          const rejectIfTargetClosest =
            typeof spec?.rejectIfTargetClosest === 'string'
              ? spec.rejectIfTargetClosest.trim()
              : '';
          const axis = typeof spec?.axis === 'string' && spec.axis.trim() ? spec.axis.trim() : 'x';
          const historyLabel =
            typeof spec?.historyLabel === 'string' ? spec.historyLabel.trim() : '';
          const vtPrefix = typeof spec?.vtPrefix === 'string' ? spec.vtPrefix.trim() : '';
          const vtSelector =
            typeof spec?.vtSelector === 'string' && spec.vtSelector.trim()
              ? spec.vtSelector.trim()
              : siblingSelector || sourceSelector;
          return sourceSelector
            ? {
                kind,
                itemSelector: itemSelector || sourceSelector,
                parentSelector,
                sourceSelector,
                siblingSelector: siblingSelector || sourceSelector,
                requiredAncestorSelector,
                rejectIfTargetClosest,
                axis,
                historyLabel,
                vtPrefix,
                vtSelector
              }
            : null;
        })
        .filter(Boolean)
    : reorderableSelectors.map((selector) => ({
        kind: '',
        itemSelector: selector,
        parentSelector: '',
        sourceSelector: selector,
        siblingSelector: selector
      }));
  const allowedFontSlugs = Array.isArray(options.allowedFontSlugs)
    ? options.allowedFontSlugs
        .map((slug) =>
          String(slug || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '')
        )
        .filter(Boolean)
    : [];
  const inlineFontRules = {};
  const inlineFontLoaders = {};
  if (options.inlineFontRules && typeof options.inlineFontRules === 'object') {
    for (const slug of allowedFontSlugs) {
      const rule = options.inlineFontRules[slug];
      if (
        typeof rule === 'string' &&
        rule.startsWith(`.pb-font-${slug}{font-family:`) &&
        !rule.includes('</style') &&
        rule.length < 500
      ) {
        inlineFontRules[slug] = rule;
      }
    }
  }
  if (options.inlineFontLoaders && typeof options.inlineFontLoaders === 'object') {
    for (const slug of allowedFontSlugs) {
      const loader = options.inlineFontLoaders[slug];
      if (loader && typeof loader.href === 'string' && /^https?:\/\//.test(loader.href)) {
        inlineFontLoaders[slug] = {
          href: loader.href,
          source: typeof loader.source === 'string' ? loader.source : ''
        };
      }
    }
  }
  const linkBindingLabels = Object.fromEntries(
    CLIENT_LINK_BINDINGS.map((binding) => [binding.key, binding.label])
  );
  const textBindingLabels = Object.fromEntries(
    CLIENT_TEXT_BINDINGS.map((binding) => [binding.key, binding.label])
  );
  return `
${formatEngineScript}
${pasteSanitizerScript}
(function() {
  'use strict';
  const __PB_NONCE__ = ${JSON.stringify(nonce)};
  var __PB_ICON_TINTING__ = ${iconTinting};
  var __PB_REORDER_SPECS__ = ${JSON.stringify(reorderableSpecs)};
  var __PB_ALLOWED_FONT_SLUGS__ = ${JSON.stringify(allowedFontSlugs)};
  var __PB_INLINE_FONT_RULES__ = ${JSON.stringify(inlineFontRules)};
  var __PB_INLINE_FONT_LOADERS__ = ${JSON.stringify(inlineFontLoaders)};
  // Canonical, priority-ordered theme colour tokens for StyleSnapshot identity
  // resolution (first exact match wins). Static core set; the parent may pass a
  // richer candidate list per query via query-style-snapshot.candidateTokens.
  var __PB_STYLE_TOKENS__ = ${JSON.stringify(TOKEN_PRIORITY)};
  const KIND_ATTR = ${JSON.stringify(KIND_ATTR)};
  const KIND_LABELS = ${JSON.stringify(KIND_LABELS)};

  const CLIENT_LINK_MARKER_PREFIX = ${JSON.stringify(CLIENT_LINK_MARKER_PREFIX)};
  const CLIENT_LINK_LABELS = ${JSON.stringify(linkBindingLabels)};
  const CLIENT_TEXT_LABELS = ${JSON.stringify(textBindingLabels)};
${coreStateLayer()}
${reorderPreviewLayer()}
${interactionLayer()}
${editingInputLayer()}
${messageHandlerLayer()}
${overlayReportersLayer()}
${collabReporterLayer()}
${scrollAnimationRuntimeScript}
`;
}
